<div class="quality-page">
  <div class="page-header">
    <div class="header-left">
      <h1>Quality</h1>
    </div>
    <div class="header-right">
      <div
        class="small-practice-indicator"
        matTooltip="This value is determined by the 'Clinicians Under 15' setting on the Settings page"
        matTooltipPosition="below"
      >
        <mat-icon class="small-practice-icon">{{ isSmallPractice ? 'check_circle' : 'cancel' }}</mat-icon>
        <span class="small-practice-label">Small Practice: {{ isSmallPractice ? 'Yes' : 'No' }}</span>
        <mat-icon class="small-practice-info-icon">info_outline</mat-icon>
      </div>
      @if (relatedOrganizations().length > 0) {
        <div class="chip tin-rollup-chip" [matTooltip]="rollupTooltip" matTooltipPosition="below">
          TIN Roll Up <mat-icon class="rollup-info-icon">info</mat-icon>
        </div>
      } @else {
        <div class="chip">Practice: {{ practice }}</div>
      }
      <div class="chip">Performance Duration: {{ performanceDuration }}</div>
      <div class="chip">TIN: {{ tin }}</div>
    </div>
  </div>

  @if (showTabs) {
    <div class="reporting-tabs">
      <button
        class="tab-btn"
        [class.active]="currentTab === 'traditional'"
        (click)="onTabChange('traditional')"
      >
        Traditional MIPS
      </button>
      <button
        class="tab-btn"
        [class.active]="currentTab === 'mvp'"
        (click)="onTabChange('mvp')"
      >
        MVP
      </button>
    </div>
  }

  <div class="validation-cards">
    <div
      class="card"
      [class.valid]="selectedMeasures.length >= minimumRequired"
    >
      <div class="card-number">{{ selectedMeasures.length }}</div>
      <div class="card-content">
        <div class="card-title">Measures Selected</div>
      </div>
    </div>

    <div class="card" [class.valid]="hasOutcomeHighPriority">
      <mat-icon class="card-icon">{{
        hasOutcomeHighPriority ? "check" : "close"
      }}</mat-icon>
      <div class="card-content">
        <div class="card-title">Outcome/High-Priority Measure Selected</div>
        <div class="card-subtitle">
          (Select Minimum 1 Outcome/High-Priority Measure)
        </div>
      </div>
    </div>

    <div class="card" [class.valid]="meetsCaseMinimum">
      <div class="card-number">{{ measuresMeetingCaseMinimum }}</div>
      <div class="card-content">
        <div class="card-title">Measures Meeting 20 Cases</div>
        <div class="card-subtitle">
          (All {{ selectedMeasures.length }} Selected Measures Should Meet
          Minimum 20 Cases)
        </div>
      </div>
    </div>

    <div class="points-card">
      <div class="points-section">
        <div class="points-label">
          Estimated Quality Points
          <mat-icon
            *ngIf="isScoreCalculating()"
            class="score-calculating-icon"
            matTooltip="Saving — scores will update momentarily"
            matTooltipPosition="above"
          >sync</mat-icon>
        </div>
        <div class="points-calculation">
          <span class="points-value">{{ estimatedPoints.toFixed(1) }}</span>
          <span class="points-operator">+</span>
          <span class="bonus-value">{{ estimatedBonus }}</span>
          <mat-icon class="info-icon" [matTooltip]="topMeasuresTooltip"
            >info_outline</mat-icon
          >
          <span class="points-operator">=</span>
          <span class="total-value"
            >{{ estimatedTotal.toFixed(1) }}/{{ maxQualityPoints }}</span
          >
        </div>
        <div class="points-sublabels">
          <span>Points</span>
          <span>Bonus</span>
          <span>Total</span>
        </div>
      </div>
      <div class="weight-section">
        <div class="weight-label">Estimated Quality Weight</div>
        <div class="weight-value">
          {{ estimatedWeight.toFixed(1) }}/{{ reweighting().weights.quality }}
          @if (showReweightingIndicator) {
            <mat-icon
              class="reweighting-icon"
              [matTooltip]="reweightingTooltip"
              matTooltipPosition="above"
            >
              info
            </mat-icon>
          }
        </div>
        @if (!showReweightingIndicator) {
          <mat-icon
            class="info-icon"
            matTooltip="Weight calculated as (Total Points × 30) / Max Points"
            >info_outline</mat-icon
          >
        }
      </div>
    </div>
  </div>

  @if (showCehrtWarning) {
    <div class="cehrt-warning-banner">
      <mat-icon class="cehrt-warning-icon">warning</mat-icon>
      <span>
        You have selected CMS eCQM measures. A validated CEHRT ID is required on the
        <strong>Settings</strong> page before submission.
      </span>
    </div>
  }

  @if (measuresLoading()) {
    <div class="measures-loading">
      <mat-spinner diameter="40"></mat-spinner>
      <span>Loading measures...</span>
    </div>
  } @else {

  <div class="table-header-row">
    <div class="search-bar">
      <mat-icon class="search-icon">search</mat-icon>
      <input
        type="text"
        class="search-input"
        placeholder="Search by Measure ID or Title..."
        [(ngModel)]="searchText"
      />
      @if (searchText) {
        <button mat-icon-button class="clear-btn" (click)="clearSearch()">
          <mat-icon>close</mat-icon>
        </button>
      }
    </div>

  </div>

  <mat-tab-group class="content-tabs">
    <mat-tab label="All">
      <table class="measures-table">
        <thead>
          <tr>
            <th class="checkbox-col"></th>
            <th class="id-col sortable" (click)="sortBy('id')">
              Registry<br />Quality ID
              @if (sortColumn === "id") {
                <mat-icon class="sort-icon">
                  {{
                    sortDirection === "asc" ? "arrow_upward" : "arrow_downward"
                  }}
                </mat-icon>
              }
            </th>
            <th class="title-col sortable" (click)="sortBy('title')">
              Measure Title
              @if (sortColumn === "title") {
                <mat-icon class="sort-icon">
                  {{
                    sortDirection === "asc" ? "arrow_upward" : "arrow_downward"
                  }}
                </mat-icon>
              }
            </th>
            <th class="numden-col">Num<br />Den</th>
            <th class="exclusion-col">Exclusion<br />Exception</th>
            <th
              class="performance-col sortable"
              (click)="sortBy('performance')"
            >
              Performance
              @if (sortColumn === "performance") {
                <mat-icon class="sort-icon">
                  {{
                    sortDirection === "asc" ? "arrow_upward" : "arrow_downward"
                  }}
                </mat-icon>
              }
            </th>
            <th class="points-col sortable" (click)="sortBy('points')">
              Points
              @if (sortColumn === "points") {
                <mat-icon class="sort-icon">
                  {{
                    sortDirection === "asc" ? "arrow_upward" : "arrow_downward"
                  }}
                </mat-icon>
              }
            </th>
            <th class="expand-col"></th>
          </tr>
        </thead>
        <tbody>
          @for (measure of filteredAndSortedMeasures; track measure.measureId) {
            <ng-container>
              <tr
                class="measure-row"
                [class.expanded]="expandedMeasureId === measure.measureId"
                (click)="toggleExpansion(measure.measureId)"
              >
                <td class="checkbox-col">
                  <mat-checkbox
                    [checked]="isMeasureSelected(measure)"
                    [disabled]="isMeasureDisabled(measure)"
                    [matTooltip]="
                      isMeasureDisabled(measure)
                        ? getDisabledTooltip(measure)
                        : ''
                    "
                    (change)="onCheckboxChange(measure, $event.checked)"
                    (click)="$event.stopPropagation()"
                  >
                  </mat-checkbox>
                </td>
                <td class="id-col">
                  <div class="id-main">{{ measure.measureId }}</div>
                </td>
                <td class="title-col">
                  <div class="measure-title">{{ measure.measureTitle }}</div>
                  <div class="measure-badges">
                    @if (measure.isMvpMeasure) {
                      <span class="badge mvp">MVP</span>
                    }
                    @if (measure.isOutcome) {
                      <span class="badge outcome">Outcome</span>
                    }
                    @if (measure.isHighPriority) {
                      <span class="badge high-priority">High Priority</span>
                    }
                    @if (measure.sevenPointCap) {
                      <span class="badge seven-point-cap">7-Point Cap</span>
                    }
                    @if (measure.noBenchmark) {
                      <span class="badge no-benchmark">No Benchmark</span>
                    }
                    @if (measure.inverse) {
                      <span class="badge inverse">Inverse</span>
                    }
                  </div>
                </td>
                <td class="numden-col">
                  <div class="fraction">
                    <div class="numerator">
                      {{ measure.numerator }}
                    </div>
                    <div class="fraction-line"></div>
                    <div class="denominator">
                      {{ measure.denominator }}
                    </div>
                  </div>
                </td>
                <td class="exclusion-col">
                  <div class="fraction">
                    <div class="numerator">
                      {{ measure.denominatorExclusion }}
                    </div>
                    <div class="fraction-line"></div>
                    <div class="denominator">
                      {{ measure.denominatorException }}
                    </div>
                  </div>
                </td>
                <td class="performance-col">
                  <div class="circular-progress">
                    <svg viewBox="0 0 60 60" class="progress-ring">
                      <circle class="progress-ring-bg" cx="30" cy="30" r="26" />
                      <circle
                        class="progress-ring-fill"
                        cx="30"
                        cy="30"
                        r="26"
                        [style.stroke-dashoffset]="
                          163.4 - (163.4 * measure.performance) / 100
                        "
                      />
                    </svg>
                    <div class="progress-text">
                      {{ measure.performance.toFixed(1) }}
                    </div>
                  </div>
                </td>
                <td class="points-col">
                  {{ measure.calculatedPoints?.toFixed(2) || "0.00" }}
                </td>
                <td class="expand-col">
                  <mat-icon>{{
                    expandedMeasureId === measure.measureId
                      ? "expand_less"
                      : "expand_more"
                  }}</mat-icon>
                </td>
              </tr>

              @if (expandedMeasureId === measure.measureId) {
                <tr class="detail-row">
                  <td colspan="8">
                    <div class="detail-panel">
                      <button
                        mat-icon-button
                        class="close-btn"
                        (click)="
                          toggleExpansion(measure.measureId);
                          $event.stopPropagation()
                        "
                      >
                        <mat-icon>close</mat-icon>
                      </button>

                      <div class="benchmark-section">
                        <div class="benchmark-item">
                          <mat-icon>arrow_upward</mat-icon>
                          <div>
                            <div class="benchmark-label">
                              Achieved Performance
                            </div>
                            <div class="benchmark-value">
                              {{ measure.achievedPerformance?.toFixed(2) }}%
                            </div>
                          </div>
                        </div>
                        <div class="benchmark-item">
                          <div class="benchmark-label">CMS Benchmark</div>
                          <div class="benchmark-value">
                            {{ measure.cmsBenchmark?.toFixed(2) || "N/A" }}%
                          </div>
                        </div>
                        <div class="benchmark-item">
                          <div class="benchmark-label">Registry Avg</div>
                          <div class="benchmark-value">
                            {{ measure.registryAvg?.toFixed(2) || "N/A" }}%
                          </div>
                        </div>
                      </div>

                      <h4>Decile Range</h4>
                      <div
                        [innerHTML]="getDecileDisplay(measure)"
                        class="decile-container"
                      ></div>
                    </div>
                  </td>
                </tr>
              }
            </ng-container>
          }
        </tbody>
      </table>
    </mat-tab>

    <mat-tab label="Selected">
      <table class="measures-table">
        <thead>
          <tr>
            <th class="checkbox-col"></th>
            <th class="id-col sortable" (click)="sortBy('id')">
              Registry<br />Quality ID
              @if (sortColumn === "id") {
                <mat-icon class="sort-icon">
                  {{
                    sortDirection === "asc" ? "arrow_upward" : "arrow_downward"
                  }}
                </mat-icon>
              }
            </th>
            <th class="title-col sortable" (click)="sortBy('title')">
              Measure Title
              @if (sortColumn === "title") {
                <mat-icon class="sort-icon">
                  {{
                    sortDirection === "asc" ? "arrow_upward" : "arrow_downward"
                  }}
                </mat-icon>
              }
            </th>
            <th class="numden-col">Num<br />Den</th>
            <th class="exclusion-col">Exclusion<br />Exception</th>
            <th
              class="performance-col sortable"
              (click)="sortBy('performance')"
            >
              Performance
              @if (sortColumn === "performance") {
                <mat-icon class="sort-icon">
                  {{
                    sortDirection === "asc" ? "arrow_upward" : "arrow_downward"
                  }}
                </mat-icon>
              }
            </th>
            <th class="points-col sortable" (click)="sortBy('points')">
              Points
              @if (sortColumn === "points") {
                <mat-icon class="sort-icon">
                  {{
                    sortDirection === "asc" ? "arrow_upward" : "arrow_downward"
                  }}
                </mat-icon>
              }
            </th>
            <th class="expand-col"></th>
          </tr>
        </thead>
        <tbody>
          @for (measure of selectedMeasures; track measure.measureId) {
            <ng-container>
              <tr
                class="measure-row"
                [class.expanded]="expandedMeasureId === measure.measureId"
                (click)="toggleExpansion(measure.measureId)"
              >
                <td class="checkbox-col">
                  <mat-checkbox
                    [checked]="isMeasureSelected(measure)"
                    (change)="onCheckboxChange(measure, $event.checked)"
                    (click)="$event.stopPropagation()"
                  >
                  </mat-checkbox>
                </td>
                <td class="id-col">
                  <div class="id-main">{{ measure.measureId }}</div>
                </td>
                <td class="title-col">
                  <div class="measure-title">{{ measure.measureTitle }}</div>
                  <div class="measure-badges">
                    @if (measure.isMvpMeasure) {
                      <span class="badge mvp">MVP</span>
                    }
                    @if (measure.isOutcome) {
                      <span class="badge outcome">Outcome</span>
                    }
                    @if (measure.isHighPriority) {
                      <span class="badge high-priority">High Priority</span>
                    }
                    @if (measure.sevenPointCap) {
                      <span class="badge seven-point-cap">7-Point Cap</span>
                    }
                    @if (measure.noBenchmark) {
                      <span class="badge no-benchmark">No Benchmark</span>
                    }
                    @if (measure.inverse) {
                      <span class="badge inverse">Inverse</span>
                    }
                  </div>
                </td>
                <td class="numden-col">
                  <div class="fraction">
                    <div class="numerator">
                      {{ measure.numerator }}
                    </div>
                    <div class="fraction-line"></div>
                    <div class="denominator">
                      {{ measure.denominator }}
                    </div>
                  </div>
                </td>
                <td class="exclusion-col">
                  <div class="fraction">
                    <div class="numerator">
                      {{ measure.denominatorExclusion }}
                    </div>
                    <div class="fraction-line"></div>
                    <div class="denominator">
                      {{ measure.denominatorException }}
                    </div>
                  </div>
                </td>
                <td class="performance-col">
                  <div class="circular-progress">
                    <svg viewBox="0 0 60 60" class="progress-ring">
                      <circle class="progress-ring-bg" cx="30" cy="30" r="26" />
                      <circle
                        class="progress-ring-fill"
                        cx="30"
                        cy="30"
                        r="26"
                        [style.stroke-dashoffset]="
                          163.4 - (163.4 * measure.performance) / 100
                        "
                      />
                    </svg>
                    <div class="progress-text">
                      {{ measure.performance.toFixed(1) }}
                    </div>
                  </div>
                </td>
                <td class="points-col">
                  {{ measure.calculatedPoints?.toFixed(2) || "0.00" }}
                </td>
                <td class="expand-col">
                  <mat-icon>{{
                    expandedMeasureId === measure.measureId
                      ? "expand_less"
                      : "expand_more"
                  }}</mat-icon>
                </td>
              </tr>

              @if (expandedMeasureId === measure.measureId) {
                <tr class="detail-row">
                  <td colspan="8">
                    <div class="detail-panel">
                      <button
                        mat-icon-button
                        class="close-btn"
                        (click)="
                          toggleExpansion(measure.measureId);
                          $event.stopPropagation()
                        "
                      >
                        <mat-icon>close</mat-icon>
                      </button>

                      <div class="benchmark-section">
                        <div class="benchmark-item">
                          <mat-icon>arrow_upward</mat-icon>
                          <div>
                            <div class="benchmark-label">
                              Achieved Performance
                            </div>
                            <div class="benchmark-value">
                              {{ measure.achievedPerformance?.toFixed(2) }}%
                            </div>
                          </div>
                        </div>
                        <div class="benchmark-item">
                          <div class="benchmark-label">CMS Benchmark</div>
                          <div class="benchmark-value">
                            {{ measure.cmsBenchmark?.toFixed(2) || "N/A" }}%
                          </div>
                        </div>
                        <div class="benchmark-item">
                          <div class="benchmark-label">Registry Avg</div>
                          <div class="benchmark-value">
                            {{ measure.registryAvg?.toFixed(2) || "N/A" }}%
                          </div>
                        </div>
                      </div>

                      <h4>Decile Range</h4>
                      <div
                        [innerHTML]="getDecileDisplay(measure)"
                        class="decile-container"
                      ></div>
                    </div>
                  </td>
                </tr>
              }
            </ng-container>
          }
        </tbody>
      </table>
    </mat-tab>
  </mat-tab-group>

  } <!-- end @else measuresLoading -->
</div>
